import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Users, Loader2, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import InstructorCard from '../components/dashboard/InstructorCard';

export default function AllInstructors({ theme, toggleTheme }) {
  const [instructors, setInstructors] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('role', '==', 'instructor'),
          where('instructorData.status', '==', 'approved')
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        docs.sort((a, b) => b.registrationDate?.toMillis() - a.registrationDate?.toMillis());
        
        setInstructors(docs);
        setFiltered(docs);
      } catch (err) {
        console.error('Error fetching instructors:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInstructors();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFiltered(instructors);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFiltered(instructors.filter(inst => 
      inst.name?.toLowerCase().includes(term) || 
      inst.instructorData?.specialization?.toLowerCase().includes(term)
    ));
  }, [searchTerm, instructors]);

  return (
    <>
      <Navbar theme={theme} toggleTheme={toggleTheme} />
      
      <main className="min-h-screen pt-32 pb-24 px-4 relative" dir="rtl">
        {/* Background Ambient */}
        <div className="absolute top-0 right-0 w-full h-[500px] bg-gradient-to-b from-primary-500/10 to-transparent pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16 px-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 font-bold mb-6 border border-primary-100 dark:border-primary-500/20">
              <Users className="w-5 h-5" />
              <span>فريق التدريس</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-6 leading-tight">
              تعرف على <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-amber-500">خـبـرائـنـا</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 dark:text-zinc-400 font-medium leading-relaxed">
              نخبة من المدربين المعتمدين والمحترفين في مجالاتهم، مستعدون لنقل خبراتهم لك وتطوير مهاراتك.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-2xl mx-auto mb-14 relative group">
            <div className="absolute inset-y-0 right-0 pr-6 flex items-center pointer-events-none">
               <Search className="w-6 h-6 text-slate-400 group-focus-within:text-primary-500 transition-colors" />
            </div>
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ابحث عن مدرب بالاسم أو التخصص..."
              className="w-full pl-6 pr-16 py-5 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-full shadow-[0_10px_40px_rgba(0,0,0,0.05)] outline-none text-lg font-bold text-slate-900 dark:text-white focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
            />
          </div>

          {/* Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-primary-500 mb-4" />
              <p className="text-lg font-bold text-slate-500">جاري تحميل المدربين...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white/50 dark:bg-black/20 rounded-3xl border border-slate-100 dark:border-white/5 backdrop-blur-xl">
              <div className="w-20 h-20 bg-slate-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-10 h-10 text-slate-400" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">لا يوجد مدربين</h2>
              <p className="text-slate-500">تأكد من كتابة الاسم أو التخصص بشكل صحيح.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filtered.map(inst => (
                <InstructorCard key={inst.id} instructor={inst} />
              ))}
            </div>
          )}

        </div>
      </main>
      
      <Footer />
    </>
  );
}
